// Meta (Facebook) Pixel helper.
//
// The pixel snippet itself lives in public/index.html and only fires one
// PageView on the initial document load. PRObroker is a SPA, so every later
// screen has to be reported from React - see trackPageView() below.
//
// Everything here is a no-op when fbq is missing (ad blockers, local dev
// without the snippet), so call sites never need their own guards.

export const META_PIXEL_ID = "299855124829636";

const getFbq = () => (typeof window !== "undefined" && typeof window.fbq === "function" ? window.fbq : null);

// Meta rejects a value that is not a finite positive number.
const toValue = (amount) => {
  const value = Number(amount);
  return Number.isFinite(value) && value > 0 ? Number(value.toFixed(2)) : undefined;
};

const clean = (params = {}) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

// Standard events (ViewContent, InitiateCheckout, AddPaymentInfo, Purchase...).
export const track = (event, params = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  try {
    fbq("track", event, clean(params));
  } catch (error) {
    console.warn("Meta Pixel track failed:", event, error?.message);
  }
};

// Anything that is not on Meta's standard event list.
export const trackCustom = (event, params = {}) => {
  const fbq = getFbq();
  if (!fbq) return;
  try {
    fbq("trackCustom", event, clean(params));
  } catch (error) {
    console.warn("Meta Pixel trackCustom failed:", event, error?.message);
  }
};

// Fire on every SPA route change so URL-based rules in Ads Manager work.
export const trackPageView = () => track("PageView");

/* ---------------------------------------------------------------------------
 * Subscription funnel
 * Each step maps to a URL so a Meta "URL contains" rule can target it too:
 *   /subscription           -> ViewContent
 *   /subscription/checkout  -> InitiateCheckout
 *   (Razorpay opened)       -> AddPaymentInfo
 *   /subscription/success   -> Purchase
 * ------------------------------------------------------------------------- */

const planParams = ({ planName, planId, months, amount }) =>
  clean({
    content_name: planName,
    content_ids: planId ? [planId] : undefined,
    content_type: "product",
    content_category: "Subscription",
    num_items: months ? Number(months) : undefined,
    value: toValue(amount),
    currency: "INR",
  });

export const trackPlansViewed = (params = {}) =>
  track("ViewContent", { content_category: "Subscription", content_name: "Subscription Plans", ...clean(params) });

export const trackInitiateCheckout = (details) => track("InitiateCheckout", planParams(details));

export const trackAddPaymentInfo = (details) => track("AddPaymentInfo", planParams(details));

export const trackPurchase = (details) => track("Purchase", planParams(details));

export const trackCouponApplied = ({ code, discount, planName }) =>
  trackCustom("CouponApplied", clean({ coupon_code: code, discount_value: toValue(discount), content_name: planName }));

export const trackPaymentFailed = ({ planName, amount, reason }) =>
  trackCustom("PaymentFailed", clean({ content_name: planName, value: toValue(amount), currency: "INR", reason }));
